import { useWindowSize } from "hooks/useWindowSize";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { BulkClassification } from "components/Transaction/BulkClassification";
import { UncategoriseModal } from "components/Transaction/UncategoriseModal";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setSelectedTransactionIds } from "store/slices/transactions";
import { useExportSelectedTransactionsMutation } from "store/apis/reports";
import { RowSelectionState } from "react-table-8.10.7";
import { ReportTxn, Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { DividerLine } from "components/icons/DividerLine";
import { Cross } from "components/icons/Cross";
import { Export } from "components/icons/Export";
import { ArrowDown } from "components/icons/ArrowDown";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import { BulkRecategorise } from "./BulkRecategorise";
import { BulkVendorAssign } from "./BulkVendorAssign";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import {
  BALANCE_SHEET,
  CASH_FLOW_STATEMENT,
  INCOME_STATEMENT,
} from "constants/bookkeeping";
import { openLink } from "utils/openLink";

const getExportSourceLabel = (row: ReportTxn) => {
  if (row.is_ledger_entry) return "Journal entry";

  const bankAccount = row.from?.bank_account;
  if (!bankAccount) return "";

  const name = bankAccount.nickname || bankAccount.bank_brand?.name || "";
  return [name, bankAccount.mask && `••${bankAccount.mask}`]
    .filter(Boolean)
    .join(" ");
};

const FINANCIAL_STATEMENT_REPORT_TYPES = [
  BALANCE_SHEET,
  INCOME_STATEMENT,
  CASH_FLOW_STATEMENT,
];

type TableSelectionControlsProps = {
  deselectRows: () => void;
  allSelectedRows: RowSelectionState;
  reportType?: string;
  hasWritePermission: boolean;
};

export const TableSelectionControls = ({
  deselectRows,
  allSelectedRows,
  reportType,
  hasWritePermission,
}: TableSelectionControlsProps) => {
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();
  const buttonProps = {
    size: "small",
  } as ButtonProps;

  const selectedTransactions = useMemo(() => {
    return Object.entries(allSelectedRows)
      .filter(([_, selected]) => selected)
      .map(([key]) => {
        return JSON.parse(key);
      });
  }, [allSelectedRows]);

  const subtotal = useMemo(
    () => selectedTransactions.reduce((sum, row) => sum + (row.amount || 0), 0),
    [selectedTransactions]
  );

  const glTransactions: Transactions[] = useMemo(() => {
    return selectedTransactions.map((t) => ({
      transaction: { ...t, merchant: t.merchant?.name || null },
    })) as unknown as Transactions[];
  }, [selectedTransactions]);

  const transactionsSelected = selectedTransactions.length > 0;
  const isIncomeStatement = reportType === INCOME_STATEMENT;
  const isFinancialStatement = FINANCIAL_STATEMENT_REPORT_TYPES.includes(
    reportType || ""
  );

  const [exportSelectedTransactions, { isLoading: isExporting }] =
    useExportSelectedTransactionsMutation();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();

  const handleExportSelectedTransactions = async (
    exportToMail: boolean,
    folderId?: string
  ) => {
    const transactions = selectedTransactions.map((t) => ({
      date: t.date,
      description: t.description || "",
      amount: t.amount || 0,
      vendor: t.merchant?.name || "",
      source: getExportSourceLabel(t),
      category_name: t.category?.name || "Uncategorised",
    }));

    try {
      const response = await exportSelectedTransactions({
        groupId,
        entityId,
        transactions,
        exportToMail,
        folderId,
      }).unwrap();

      if (folderId && "document_id" in response) {
        setSavedDocumentId(response.document_id);
        savedToInkleDocsModal.open();
        return;
      }

      if ("download_url" in response) {
        openLink(response.download_url);
        successToast({ message: "Selected transactions downloaded" });
        return;
      }

      successToast({ message: response.message });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  const bulkRecategoriseModal = useModal();
  const bulkAssignVendorModal = useModal();
  const bulkClassificationModal = useModal();
  const bulkUncategoriseModal = useModal();
  const subscriptionModal = useModal();
  const { isMobile } = useWindowSize();
  const classificationPermission = useHasSubscriptionPermission({
    feature: CLASSIFICATION_FEATURE_ACCESS,
  });

  const handleClassificationSelect = () => {
    if (classificationPermission?.blocked) {
      subscriptionModal.open();
      return;
    }
    bulkClassificationModal.open();
  };

  const openUncategoriseModal = () => {
    dispatch(
      setSelectedTransactionIds(selectedTransactions.map((t) => t.uuid))
    );
    bulkUncategoriseModal.open();
  };

  return (
    <>
      <SelectedFloatingControl>
        {selectedTransactions?.length > 0 && (
          <>
            <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
              {selectedTransactions?.length} selected
            </span>
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            <>
              <div className="t-flex t-items-center t-gap-1 t-text-text-60 t-text-subtext">
                <span>Subtotal:</span>
                <AnimatedNumbers amount={subtotal} />
              </div>
              <span className="t-text-neutral-10 t-h-full">
                <DividerLine />
              </span>
            </>
            <motion.span
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ease: "easeInOut", duration: 0.2 }}
              key={transactionsSelected.toString()}
              className="t-flex t-gap-3 t-items-center"
            >
              {isMobile && (
                <LockedUi
                  hasAccess={hasWritePermission}
                  blockedUi={<LockedButton text="Actions" />}
                >
                  <Dropdown.Root>
                    <Dropdown.Trigger asChild className="t-group">
                      <div>
                        <Button {...buttonProps}>
                          <span className="t-flex t-items-center t-gap-1">
                            Actions
                            <ArrowDown color="currentColor" size="12" />
                          </span>
                        </Button>
                      </div>
                    </Dropdown.Trigger>
                    <Dropdown.Portal>
                      <Dropdown.Content
                        side="top"
                        align="end"
                        sideOffset={8}
                        collisionPadding={12}
                      >
                        <Dropdown.Item onSelect={bulkAssignVendorModal.open}>
                          Assign vendor
                        </Dropdown.Item>
                        <Dropdown.Item onSelect={bulkRecategoriseModal.open}>
                          Recategorise
                        </Dropdown.Item>
                        <Dropdown.Item onSelect={openUncategoriseModal}>
                          Uncategorise
                        </Dropdown.Item>
                        {isIncomeStatement && (
                          <Dropdown.Item onSelect={handleClassificationSelect}>
                            Classification
                          </Dropdown.Item>
                        )}
                        {isFinancialStatement && (
                          <>
                            <Dropdown.Separator />
                            <Dropdown.Item
                              disabled={isExporting}
                              onSelect={() =>
                                handleExportSelectedTransactions(false)
                              }
                            >
                              Download Excel
                            </Dropdown.Item>
                            <Dropdown.Item
                              disabled={isExporting}
                              onSelect={() =>
                                handleExportSelectedTransactions(true)
                              }
                            >
                              Email Excel
                            </Dropdown.Item>
                            <Dropdown.Item onSelect={selectFolderModal.open}>
                              Save in Inkle Documents
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Content>
                    </Dropdown.Portal>
                  </Dropdown.Root>
                </LockedUi>
              )}
              {!isMobile && (
                <>
                  <LockedUi
                    hasAccess={hasWritePermission}
                    blockedUi={<LockedButton text="Assign vendor" />}
                  >
                    <Button
                      {...buttonProps}
                      onClick={bulkAssignVendorModal.open}
                    >
                      Assign vendor
                    </Button>
                  </LockedUi>
                  <LockedUi
                    hasAccess={hasWritePermission}
                    blockedUi={<LockedButton text="Recategorise" />}
                  >
                    <Button
                      {...buttonProps}
                      onClick={bulkRecategoriseModal.open}
                    >
                      Recategorise
                    </Button>
                  </LockedUi>
                  <LockedUi
                    hasAccess={hasWritePermission}
                    blockedUi={<LockedButton text="Uncategorise" />}
                  >
                    <Button {...buttonProps} onClick={openUncategoriseModal}>
                      Uncategorise
                    </Button>
                  </LockedUi>
                  {isIncomeStatement && (
                    <LockedUi
                      hasAccess={hasWritePermission}
                      blockedUi={<LockedButton text="Classification" />}
                    >
                      <PermissionBasedUI
                        feature={CLASSIFICATION_FEATURE_ACCESS}
                        entityId={entityId}
                        onUpgrade={subscriptionModal.open}
                        blockedUI={
                          <Button
                            {...buttonProps}
                            rightIcon={<DiamondPurple height={13} width={14} />}
                          >
                            Classification
                          </Button>
                        }
                      >
                        <Button
                          {...buttonProps}
                          onClick={bulkClassificationModal.open}
                        >
                          Classification
                        </Button>
                      </PermissionBasedUI>
                    </LockedUi>
                  )}
                  {isFinancialStatement && (
                    <Dropdown.Root>
                      <Dropdown.Trigger asChild disabled={isExporting}>
                        <Button
                          {...buttonProps}
                          leftIcon={<Export />}
                          rightIcon={
                            <ArrowDown color="currentColor" size="12" />
                          }
                          isLoading={isExporting}
                          disabled={isExporting}
                        >
                          Download Excel
                        </Button>
                      </Dropdown.Trigger>
                      <Dropdown.Portal>
                        <Dropdown.Content
                          sideOffset={4}
                          align="end"
                          side="bottom"
                        >
                          <ExportOptions
                            onLocalDownload={() =>
                              handleExportSelectedTransactions(false)
                            }
                            onMailExport={() =>
                              handleExportSelectedTransactions(true)
                            }
                            onSaveInInkleDocs={selectFolderModal.open}
                          />
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  )}
                </>
              )}
            </motion.span>
            <Button
              customType="ghost_icon"
              size="xsmall"
              onClick={deselectRows}
            >
              <Cross />
            </Button>
          </>
        )}
      </SelectedFloatingControl>

      {bulkRecategoriseModal.isOpen && (
        <BulkRecategorise
          deselectRows={deselectRows}
          close={bulkRecategoriseModal.close}
          isOpen={bulkRecategoriseModal.isOpen}
          transactions={allSelectedRows}
        />
      )}

      {bulkAssignVendorModal.isOpen && (
        <BulkVendorAssign
          deselectRows={deselectRows}
          close={bulkAssignVendorModal.close}
          isOpen={bulkAssignVendorModal.isOpen}
          transactions={allSelectedRows}
        />
      )}

      {bulkClassificationModal.isOpen && (
        <BulkClassification
          deselectRows={deselectRows}
          close={bulkClassificationModal.close}
          isOpen={bulkClassificationModal.isOpen}
          allSelectedRows={selectedTransactions}
        />
      )}

      {bulkUncategoriseModal.isOpen && (
        <UncategoriseModal
          deselectRows={deselectRows}
          close={bulkUncategoriseModal.close}
          isOpen={bulkUncategoriseModal.isOpen}
          isAllTxnSelected={false}
          unSelectedRowsIds={[]}
          transactions={glTransactions}
        />
      )}

      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) =>
            handleExportSelectedTransactions(false, folderId)
          }
        />
      )}

      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="Selected transactions"
        />
      )}

      <BooksSoftwareSubscriptionsModal
        open={subscriptionModal.isOpen}
        onClose={subscriptionModal.close}
        entityId={entityId}
        onProductSubscribe={subscriptionModal.close}
        modalDescription={
          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
        }
      />
    </>
  );
};
